import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtTimeAgo } from "@/lib/labels";
import { Plus, Route as RouteIcon, AlertTriangle } from "lucide-react";
import { Sparkline, pct } from "@/components/journeys/FunnelChart";

const STATUS_BADGE = { active: "b-healthy", paused: "b-muted", draft: "b-muted" };

export default function Journeys() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const { permissions } = useAuth();
  const canManage = permissions.includes("journey.manage");
  const nav = useNavigate();

  useEffect(() => {
    api.get("/journeys").then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.message || "Could not load journeys"));
  }, []);

  if (error) return <div className="p-8 text-[13px]" style={{ color: "var(--text-muted)" }}>{error}</div>;
  if (!data) return <div className="p-8 text-[13px]" style={{ color: "var(--text-muted)" }}>Loading…</div>;

  const atLimit = data.limit != null && data.journeys.length >= data.limit;

  return (
    <div className="px-4 py-5 md:px-8 md:py-6">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--text-muted)" }}>Customer journeys</div>
          <h1 className="font-display text-[26px]">What customers should get through</h1>
          <p className="text-[13px] mt-1 max-w-[720px]" style={{ color: "var(--text-secondary)" }}>
            Each journey is a playbook of steps, built from the events your product already sends.
            Prioris follows every one of them and opens a case when a step stops converting.
          </p>
        </div>
        {data.enabled && canManage && (
          <button className="btn" disabled={atLimit} data-testid="new-journey"
                  title={atLimit ? `Your plan includes ${data.limit} journeys` : undefined}
                  onClick={() => nav("/journeys/new")}>
            <Plus size={13} /> New journey
          </button>
        )}
      </div>

      {!data.enabled && (
        <div className="card p-5 mb-5" data-testid="journeys-locked">
          <div className="text-[13px]">Journeys are not included in this plan.</div>
          <div className="text-[12px] mt-1" style={{ color: "var(--text-muted)" }}>
            They need a product analytics connector and a plan that includes journey monitoring.
          </div>
        </div>
      )}

      {data.enabled && data.journeys.length === 0 && (
        <div className="card p-6" data-testid="journeys-empty">
          <RouteIcon size={18} style={{ color: "var(--text-secondary)" }} />
          <div className="text-[13.5px] font-medium mt-2">No journeys yet</div>
          <p className="text-[12.5px] mt-1 max-w-[560px]" style={{ color: "var(--text-secondary)" }}>
            Connect your analytics under <Link to="/connectors" className="underline">Connectors</Link>{" "}
            (Segment, RudderStack or PostHog), then define the steps of a journey such as
            signup, onboarding or checkout. The editor shows the funnel before you save it.
          </p>
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {data.journeys.map((j) => {
          const broken = j.latest?.broken_step_key;
          const brokenStep = broken && j.steps.find((s) => s.key === broken);
          const last = j.trend?.length ? j.trend[j.trend.length - 1].completion : null;
          return (
            <Link key={j.id} to={`/journeys/${j.id}`} className="card p-4 block hover:opacity-95"
                  data-testid={`journey-${j.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-[14px] truncate">{j.name}</span>
                <span className={`badge ${STATUS_BADGE[j.status] || "b-muted"} ml-auto`}>{j.status}</span>
              </div>
              <div className="text-[11.5px] truncate mb-3" style={{ color: "var(--text-muted)" }}>
                {j.steps.map((s) => s.label).join(" → ")}
              </div>
              <div className="flex items-end justify-between gap-3">
                <div>
                  <div className="text-[10.5px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Completion</div>
                  <div className="font-mono text-[20px] tabular-nums">{pct(last)}</div>
                </div>
                <Sparkline points={j.trend} />
              </div>
              <div className="mt-3 text-[11.5px] flex items-center gap-2" style={{ color: "var(--text-secondary)" }}>
                {brokenStep ? (
                  <span className="badge b-rev" data-testid="journey-broken">
                    <AlertTriangle size={10} className="inline mr-0.5" />
                    {brokenStep.label} down {Math.round(j.latest.drop_pct)}%
                  </span>
                ) : j.last_evaluated_at ? (
                  <span>Healthy · checked {fmtTimeAgo(j.last_evaluated_at)}</span>
                ) : (
                  <span>Not evaluated yet</span>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
